import apiUrls from "../config/apiUrls";
import IApiResponse from "../types/IApiResponse";
import { IPlayerAttributesData } from "../types/IPlayerAttributesData";
import { IPlayerSearchResponse, IPlayerZonePosition } from "../types/IPlayerSearchResponse";
import { IPositionSearchByZoneResponse } from "../types/IPositionSearchByZoneResponse";
import { IRegionAreaModel, IRegionCompetitionsModel } from "../types/IRegionAreaData";
import { fetchData, postData } from "./api";

// Helper function to handle an error response from the API
const handleErrorResponse = <T>(response: IApiResponse<any>): IApiResponse<T> => {
    console.error("Error:", response.error?.message);

    return {
        isSuccess: false,
        data: ([] as unknown as T),
        statusCode: response.statusCode || '500', // Default to 500 if status code is not available
        additionalMetadata: response.additionalMetadata || null,
        responseMessage: response.responseMessage || 'Error occurred',
        error: {
            code: response.error?.code || 'Unknown_Error',
            message: response.error?.message || 'Unknown error occurred'
        }
    };
};

// Helper function to create a standardized error response for failed API calls
const createErrorResponse = <T>(statusCode: string, message: string): IApiResponse<T> => {
    console.error('API call error:', message);

    return {
        isSuccess: false,
        // If T is expected to be an array, return an empty array, otherwise, use null
        data: ([] as unknown as T),
        statusCode,
        additionalMetadata: null,
        responseMessage: 'Internal Server Error',
        error: {
            code: statusCode,
            message
        }
    };
};

export const fetchAllAreaByRegions = async (): Promise<IApiResponse<IRegionAreaModel[]>> => {
    try {
        const response: IApiResponse<IRegionAreaModel[]> = await fetchData(`${apiUrls['GetAllAreaByRegions']}`);

        if (response.isSuccess) {
            return response;
        }

        return handleErrorResponse<IRegionAreaModel[]>(response);
    } catch (error) {
        return createErrorResponse<IRegionAreaModel[]>('500', 'API call failed due to an internal server error');
    }
};

export const fetchAllClubLeagues = async (): Promise<IApiResponse<IRegionCompetitionsModel[]>> => {
    try {
        const response: IApiResponse<IRegionCompetitionsModel[]> = await fetchData(`${apiUrls['GetAllCompetitionsByRegions']}`);

        if (response.isSuccess) {
            return response;
        }

        return handleErrorResponse<IRegionCompetitionsModel[]>(response);
    } catch (error) {
        return createErrorResponse<IRegionCompetitionsModel[]>('500', 'API call failed due to an internal server error');
    }
};

export const fetchDataForSearchPlayer = async (searchValue: string): Promise<IApiResponse<IPlayerSearchResponse[]>> => {
    try {
        const response: IApiResponse<IPlayerSearchResponse[]> = await fetchData(`${apiUrls['PlayerSearch']}?searchQuery=${searchValue}`);

        if (response.isSuccess) {
            return response;
        }

        return handleErrorResponse<IPlayerSearchResponse[]>(response);
    } catch (error) {
        return createErrorResponse<IPlayerSearchResponse[]>('500', 'API call failed due to an internal server error');
    }
}

export const fetchSearchByPosition = async (requestParameters: any): Promise<IApiResponse<IPositionSearchByZoneResponse[]>> => {
    try {
        const response: IApiResponse<IPositionSearchByZoneResponse[]> = await postData(
            apiUrls.SearchPositionbyZone,
            requestParameters
        );

        if (response.isSuccess) {
            return response; // Return successful response
        }
        // If the API returns an error, handle it gracefully
        return handleErrorResponse(response);
    } catch (error) {
        return createErrorResponse('500', 'API call failed due to an internal server error');
    }
};

export const fetchPlayerZonePositionData = async (playerId: number): Promise<IApiResponse<IPlayerZonePosition>> => {
    try {
        const response: IApiResponse<IPlayerZonePosition> = await fetchData(
            `${apiUrls['GetPlayerZonePositions']}?PlayerId=${playerId}`
        );

        if (response.isSuccess) {
            return response; // Return the successful response
        }

        // Handle error response gracefully
        return handleErrorResponse<IPlayerZonePosition>(response);
    } catch (error) {
        // Handle exceptions such as network errors
        return createErrorResponse<IPlayerZonePosition>('500', 'API call failed due to an internal server error');
    }
};

export const GetPlayerAttributes = async (requestParameters: any): Promise<IApiResponse<IPlayerAttributesData>> => {
    try {
        const response: IApiResponse<IPlayerAttributesData> = await postData(
            apiUrls.GetPlayerAttributes, requestParameters
        );

        if (response.isSuccess) {
            return response; // Return successful response
        }
        // If the API returns an error, handle it gracefully
        return handleErrorResponse(response);
    } catch (error) {
        return createErrorResponse('500', 'API call failed due to an internal server error');
    }
};

